import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { DateTime } from 'luxon';
import { useFilter } from '@/data/store/useFilterStore';

export default function useCheckinData({ eventInstanceID, checkinLocation, labelQueue }) {
    const queryClient = useQueryClient();
    const filterStore = useFilter('checkin');

    const [selectedFilter, setSelectedFilter] = useState("Everyone");
    const [availablePeople, setAvailablePeople] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [filterAttendanceSnapshot, setFilterAttendanceSnapshot] = useState([]);

    // Track if we've ever loaded data to prevent clearing during refetch
    const hasLoadedDataRef = useRef(false);

    const { data: eventInstance, isLoading: eventInstanceLoading } = Query.eventInstances.byId({ id: eventInstanceID });
    const { data: attendance, isFetching: fetchingAttendance } = Query.eventInstances.attendance({
        eventInstanceID: eventInstance?.id,
        options: { enabled: !!eventInstance?.id },
    });

    // Clear availablePeople when switching to Awaiting Print so merge starts from empty
    useEffect(() => {
        if (selectedFilter === "Awaiting Print") {
            setAvailablePeople([]);
        }
    }, [selectedFilter]);

    // Snapshot attendance IDs when a filter that needs ID-based querying is active.
    // "Checked In" is scoped to the selected location (if any); "Not Checked In" uses all
    // attendance IDs since not-checked-in is location-independent.
    // Awaiting Print is built directly from attendance records — no snapshot needed.
    //
    // IMPORTANT: only re-snapshot on (a) filter change, (b) location change, or
    // (c) server fetch completion (fetchingAttendance true→false). We intentionally
    // do NOT re-snapshot on every `attendance` ref change, because optimistic and
    // realtime `setQueryData` calls mutate the attendance cache without a server
    // refetch. Re-snapshotting on those would change `filterAttendanceSnapshot`,
    // change the people query key, and trigger a fresh infinite query — resetting
    // pagination back to page 1 and collapsing the loaded list, which makes
    // LegendList jump to the top on "Not Checked In"/"Checked In" check-ins.
    // The client-side safety filter in processData handles the stale-snapshot gap
    // between optimistic actions and the next server refresh.
    const prevFetchingRef = useRef(true);
    const prevFilterRef = useRef(selectedFilter);
    const prevLocationRef = useRef(checkinLocation?.name);

    useEffect(() => {
        const filterChanged = prevFilterRef.current !== selectedFilter;
        const locationChanged = prevLocationRef.current !== checkinLocation?.name;
        const fetchJustFinished = prevFetchingRef.current && !fetchingAttendance;
        prevFetchingRef.current = fetchingAttendance;
        prevFilterRef.current = selectedFilter;
        prevLocationRef.current = checkinLocation?.name;

        if (fetchingAttendance || !attendance) return;
        if (!filterChanged && !locationChanged && !fetchJustFinished) return;

        if (selectedFilter === "Checked In") {
            const scoped = checkinLocation?.name
                ? attendance?.filter(a => a.Location?.name === checkinLocation.name)
                : attendance;
            setFilterAttendanceSnapshot(scoped?.map(a => a.attendeeID) || []);
        } else if (selectedFilter === "Not Checked In") {
            setFilterAttendanceSnapshot(attendance?.map(a => a.attendeeID) || []);
        }
    }, [selectedFilter, fetchingAttendance, checkinLocation?.name, attendance]);

    const everyoneFilters = useMemo(() => {
        if (selectedFilter === "Checked In") {
            return { id: filterAttendanceSnapshot.length > 0 ? filterAttendanceSnapshot : [], includeFamily: true, includeAddressPeople: true };
        }
        if (selectedFilter === "Not Checked In") {
            return { notid: filterAttendanceSnapshot, includeFamily: true, includeAddressPeople: true };
        }
        return { ...filterStore.peopleFilters, includeFamily: true, includeAddressPeople: true };
    }, [filterStore.peopleFilters, selectedFilter, filterAttendanceSnapshot]);

    const everyoneFilterLogic = (selectedFilter === "Checked In" || selectedFilter === "Not Checked In") ? "AND" : filterStore.filterLogic;

    const {
        data: people, isLoading: peopleLoading, isFetching: fetchingEveryone,
        fetchNextPage: fetchNextPageEveryone, hasNextPage: hasNextPageEveryone,
    } = Query.people.all({
        searchTerm: searchText, filters: everyoneFilters, filterLogic: everyoneFilterLogic,
        options: { enabled: ["Everyone", "Checked In", "Not Checked In"].includes(selectedFilter) && !eventInstanceLoading },
    });

    const {
        data: recentPeople, isLoading: loadingRecent, isFetching: fetchingRecent,
        fetchNextPage: fetchNextPageRecent, hasNextPage: hasNextPageRecent,
    } = Query.people.recentAttendance({
        eventID: eventInstance?.Event?.id,
        searchTerm: searchText,
        eventInstanceID,
        options: { enabled: selectedFilter === "Recent" && !!eventInstance?.Event?.id },
    });

    const {
        data: suggestedPeople, isLoading: loadingSuggested, isFetching: fetchingSuggested,
        fetchNextPage: fetchNextPageSuggested, hasNextPage: hasNextPageSuggested,
    } = Query.people.all({
        searchTerm: searchText,
        options: { enabled: selectedFilter === "Suggested" && !eventInstanceLoading },
        filters: { ...checkinLocation?.CheckInFilter?.filterJSON, includeFamily: true, includeAddressPeople: true },
        filterLogic: checkinLocation?.CheckInFilter?.logic,
    });

    const isFetching = fetchingEveryone || fetchingRecent || fetchingSuggested;
    const isLoading = peopleLoading || loadingRecent || loadingSuggested;

    const fetchNextPage = useMemo(() => {
        if (["Everyone", "Checked In", "Not Checked In"].includes(selectedFilter)) return fetchNextPageEveryone;
        if (selectedFilter === "Recent") return fetchNextPageRecent;
        return fetchNextPageSuggested;
    }, [selectedFilter, fetchNextPageEveryone, fetchNextPageRecent, fetchNextPageSuggested]);

    const hasNextPage = useMemo(() => {
        if (["Everyone", "Checked In", "Not Checked In"].includes(selectedFilter)) return hasNextPageEveryone;
        if (selectedFilter === "Recent") return hasNextPageRecent;
        return hasNextPageSuggested;
    }, [selectedFilter, hasNextPageEveryone, hasNextPageRecent, hasNextPageSuggested]);

    // Process raw query data into enriched availablePeople — immutably.
    // Reads attendance and labelQueue from closure so it does NOT rerun on
    // every attendance/labelQueue change. Optimistic updates in useCheckinActions
    // handle the immediate UI; this only runs on people/filter data changes.
    const processData = () => {
        let allPeople = [];
        let count = 0;

        switch (selectedFilter) {
            case "Recent":
                count = recentPeople?.pages?.[0]?.count || 0;
                allPeople = recentPeople?.pages?.flatMap(page => page.data) || [];
                break;
            case "Suggested":
                count = suggestedPeople?.pages?.[0]?.count || 0;
                allPeople = suggestedPeople?.pages?.flatMap(page => page.data) || [];
                break;
            default:
                count = people?.pages?.[0]?.count || 0;
                allPeople = people?.pages?.flatMap(page => page.data) || [];
                break;
        }

        // O(1) lookup indexes — avoids per-person .find/.some scans over
        // attendance and labelQueue (was O(people × attendance)).
        // First-match semantics preserved: skip set() when key already exists.
        const attendanceByAttendee = new Map();
        for (const a of attendance || []) {
            if (!attendanceByAttendee.has(a?.attendeeID)) attendanceByAttendee.set(a?.attendeeID, a);
        }
        const labelQueueIDs = new Set((labelQueue || []).map(lq => lq?.id));

        // For Awaiting Print: build from attendance records, but merge stably into existing list
        // to avoid full re-render flicker on realtime updates.
        if (selectedFilter === "Awaiting Print") {
            const awaitingRecords = (attendance || [])
                .filter(a => a.Location?.name === checkinLocation?.name && !a.labelPrinted && !a.checkedOutAt);

            const awaitingIDs = new Set(awaitingRecords.map(r => r.attendeeID));
            const awaitingByAttendee = new Map();
            for (const r of awaitingRecords) {
                if (!awaitingByAttendee.has(r.attendeeID)) awaitingByAttendee.set(r.attendeeID, r);
            }

            const buildPerson = (record, existing = null) => {
                const isPending = labelQueueIDs.has(record.attendeeID);
                const firstName = record.Attendee?.firstName || existing?.firstName || "";
                const lastName = record.Attendee?.lastName || existing?.lastName || "";
                return {
                    ...(existing || {}),
                    id: record.attendeeID,
                    firstName,
                    lastName,
                    dateOfBirth: record.Attendee?.dateOfBirth || existing?.dateOfBirth || null,
                    avatarUpdatedAt: record.Attendee?.avatarUpdatedAt || existing?.avatarUpdatedAt || null,
                    avatarStorageKey: record.Attendee?.avatarStorageKey || existing?.avatarStorageKey || null,
                    isSelected: true,
                    isCheckedOut: false,
                    checkedOutAt: null,
                    labelPrinted: false,
                    labelPrintCode: record.labelPrintCode || null,
                    isPending,
                    isProcessing: false,
                    subtitle: ((record.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "")) ? ((record.Location?.name || "No Location Specified") + " \u2022 ") : "") + DateTime.fromISO(record.createdAt).toLocaleString(DateTime.DATETIME_MED),
                    selectionDetail: record.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : ""),
                    checkInLocation: record.Location?.name || null,
                    checkInTime: record.createdAt || null,
                    submitterName: record.Submitter ? `${record.Submitter.firstName} ${record.Submitter.lastName}` : (existing?.submitterName || null),
                    Family: null,
                    Address: null,
                };
            };

            setTotalCount(awaitingRecords.length);
            setAvailablePeople(prev => {
                // Remove people who are no longer awaiting (printed or checked out)
                const kept = prev.filter(p => awaitingIDs.has(p.id));
                const keptIDs = new Set(kept.map(p => p.id));

                // Update existing people in-place (preserves list order, avoids remount)
                const updated = kept.map(p => {
                    const record = awaitingByAttendee.get(p.id);
                    return record ? buildPerson(record, p) : p;
                });

                // Append new arrivals at the bottom, sorted by check-in time among themselves
                const newRecords = awaitingRecords
                    .filter(r => !keptIDs.has(r.attendeeID))
                    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                const newPeople = newRecords.map(r => buildPerson(r, null));

                return [...updated, ...newPeople];
            });
            return;
        }

        // Enrich a single person with attendance/label data
        const enrichPerson = (p) => {
            if (!p) return {};

            const record = attendanceByAttendee.get(p.id);
            const isPending = labelQueueIDs.has(p.id);

            let subtitle = "";
            if (record?.checkedOutAt) {
                subtitle = `Checked out at ${DateTime.fromISO(record.checkedOutAt).toLocaleString(DateTime.TIME_SIMPLE)}`;
            } else if (record) {
                const locationLabel = record.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "");
                subtitle = (locationLabel ? locationLabel + " • " : "") + DateTime.fromISO(record.createdAt).toLocaleString(DateTime.DATETIME_MED);
                if (record.labelPrinted) subtitle += " • Printed" + (record.labelPrintCode ? ` (${record.labelPrintCode})` : "");
            }

            const familyMembers = p.Family?.FamilyMembers?.length > 0
                ? p.Family.FamilyMembers.map(fm => {
                    const fmRecord = attendanceByAttendee.get(fm?.id);
                    return {
                        ...fm,
                        isSelected: !!fmRecord,
                        isCheckedOut: !!fmRecord?.checkedOutAt,
                        labelPrinted: fmRecord?.labelPrinted || false,
                        labelPrintCode: fmRecord?.labelPrintCode || null,
                        selectionDetail: fmRecord?.checkedOutAt ? "Checked out" : (fmRecord ? (fmRecord.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "")) : ""),
                        isPending: labelQueueIDs.has(fm?.id),
                        submitterName: fmRecord?.Submitter ? `${fmRecord.Submitter.firstName} ${fmRecord.Submitter.lastName}` : null,
                        checkInTime: fmRecord?.createdAt || null,
                        checkInLocation: fmRecord?.Location?.name || null,
                    };
                })
                : p.Family?.FamilyMembers;

            const addressPeople = p.Address?.People?.length > 0
                ? p.Address.People.map(ap => {
                    const apRecord = attendanceByAttendee.get(ap?.id);
                    return {
                        ...ap,
                        isSelected: !!apRecord,
                        isCheckedOut: !!apRecord?.checkedOutAt,
                        labelPrinted: apRecord?.labelPrinted || false,
                        labelPrintCode: apRecord?.labelPrintCode || null,
                        selectionDetail: apRecord?.checkedOutAt ? "Checked out" : (apRecord ? (apRecord.Location?.name || (eventInstance?.Locations?.length > 0 ? "No Location Specified" : "")) : ""),
                        isPending: labelQueueIDs.has(ap?.id),
                        submitterName: apRecord?.Submitter ? `${apRecord.Submitter.firstName} ${apRecord.Submitter.lastName}` : null,
                        checkInTime: apRecord?.createdAt || null,
                        checkInLocation: apRecord?.Location?.name || null,
                    };
                })
                : p.Address?.People;

            return {
                ...p,
                isSelected: !!record,
                isCheckedOut: !!record?.checkedOutAt,
                labelPrinted: record?.labelPrinted || false,
                labelPrintCode: record?.labelPrintCode || null,
                checkedOutAt: record?.checkedOutAt || null,
                isPending,
                subtitle,
                submitterName: record?.Submitter ? `${record.Submitter.firstName} ${record.Submitter.lastName}` : null,
                checkInTime: record?.createdAt || null,
                checkInLocation: record?.Location?.name || null,
                Family: familyMembers !== p.Family?.FamilyMembers ? { ...p.Family, FamilyMembers: familyMembers } : p.Family,
                Address: addressPeople !== p.Address?.People ? { ...p.Address, People: addressPeople } : p.Address,
            };
        };

        // Client-side safety filter for "Checked In"/"Not Checked In".
        // The server snapshot (filterAttendanceSnapshot) is only refreshed on
        // filter/location change or server fetch completion — NOT on every
        // optimistic/realtime attendance update (see snapshot effect above).
        // Between optimistic actions and the next server refresh the snapshot is
        // stale, so the people query may still return someone who was just
        // checked in (Not Checked In) or checked out/removed (Checked In). Drop
        // them here so pagination never re-adds them to the list.
        if (selectedFilter === "Not Checked In") {
            const checkedInIDs = new Set((attendance || []).map(a => a?.attendeeID));
            allPeople = allPeople.filter(p => !checkedInIDs.has(p.id));
        } else if (selectedFilter === "Checked In") {
            const scopedAttendance = checkinLocation?.name
                ? (attendance || []).filter(a => a?.Location?.name === checkinLocation.name)
                : (attendance || []);
            const scopedIDs = new Set(scopedAttendance.map(a => a?.attendeeID));
            allPeople = allPeople.filter(p => scopedIDs.has(p.id));
        }

        // Stable merge: preserve object references for unchanged items to prevent
        // list rerender/scroll-to-top. New items are enriched and appended at the
        // bottom; items no longer in the query data are dropped.
        const newIDs = new Set(allPeople.map(p => p.id));
        setTotalCount(count);
        setAvailablePeople(prev => {
            const kept = prev.filter(p => newIDs.has(p.id));
            const keptIDs = new Set(kept.map(p => p.id));

            // Re-enrich kept people only if their base profile data changed in the
            // query. Otherwise preserve the existing reference — optimistic updates
            // in useCheckinActions already applied attendance-related field changes.
            const updated = kept.map(p => {
                const fresh = allPeople.find(fp => fp.id === p.id);
                if (!fresh) return p;
                if (
                    fresh.firstName === p.firstName &&
                    fresh.lastName === p.lastName &&
                    fresh.avatarUpdatedAt === p.avatarUpdatedAt &&
                    fresh.avatarStorageKey === p.avatarStorageKey &&
                    fresh.dateOfBirth === p.dateOfBirth
                ) {
                    return p;
                }
                return enrichPerson(fresh);
            });

            const newPeople = allPeople
                .filter(p => !keptIDs.has(p.id))
                .map(p => enrichPerson(p));

            return [...updated, ...newPeople];
        });

        if (allPeople.length > 0) {
            hasLoadedDataRef.current = true;
        }
    };

    useEffect(() => {
        // Skip during refetch — optimistic updates handle attendance-related UI;
        // processData runs when people/query data actually changes.
        if (isFetching) return;
        processData();
    }, [people, recentPeople, suggestedPeople, selectedFilter, searchText, filterStore.peopleFilters, filterStore.filterLogic, checkinLocation, isFetching]);

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.recentAttendance(eventInstance?.Event?.id) });
    }, []);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.attendance(eventInstance?.id) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.people.recentAttendance(eventInstance?.Event?.id) });
    }, [eventInstance?.id, eventInstance?.Event?.id]);

    return {
        eventInstance,
        eventInstanceLoading,
        attendance,
        fetchingAttendance,
        availablePeople,
        setAvailablePeople,
        searchText,
        setSearchText,
        selectedFilter,
        setSelectedFilter,
        totalCount,
        isFetching,
        isLoading,
        fetchNextPage,
        hasNextPage,
        refreshList,
        filterStore,
        everyoneFilters,
        everyoneFilterLogic,
    };
}
